/**
 * The two actions with extra structure, composed from `ActionDialog` rather
 * than replacing it (§05.4, tasks C5 and C6).
 *
 * Composition rather than a second dialog is the whole point: if these were
 * written standalone they would each need their own copy of the target list,
 * the count, the consequence sentence and the type-to-confirm gate — four more
 * chances to omit one, in exactly the two actions where omitting one matters
 * most.
 */

import { DISCONNECT_REASONS, type PlanSummary, type SimSummary } from '@rd/shared';
import { useState, type ReactElement } from 'react';

import { Field } from '../../../components/Field.js';
import { Input } from '../../../components/Input.js';
import type { ApiClientError } from '../../../lib/apiClient.js';

import { ActionDialog } from './ActionDialog.js';
import { SIM_ACTIONS, type SimActionDef } from './actionRegistry.js';
import { PlanPicker } from './PlanPicker.js';

export interface CommonDialogProps {
  open: boolean;
  targets: SimSummary[];
  excluded?: SimSummary[];
  busy?: boolean;
  error?: ApiClientError | null;
  onCancel(): void;
}

/** Disconnect — the 9-value reason is required by the upstream, plus consent. */
export function DisconnectDialog(
  props: CommonDialogProps & { onConfirm(input: { reason: string; consent: boolean }): void },
): ReactElement | null {
  const [reason, setReason] = useState('');
  const [consent, setConsent] = useState(false);

  return (
    <ActionDialog
      action={SIM_ACTIONS.disconnect}
      open={props.open}
      targets={props.targets}
      {...(props.excluded ? { excluded: props.excluded } : {})}
      reason={reason}
      onReasonChange={setReason}
      reasonOptions={DISCONNECT_REASONS}
      consent={consent}
      onConsentChange={setConsent}
      {...(props.busy === undefined ? {} : { busy: props.busy })}
      {...(props.error === undefined ? {} : { error: props.error })}
      onConfirm={() => props.onConfirm({ reason, consent })}
      onCancel={props.onCancel}
    />
  );
}

/**
 * Swap — the API takes `{ existingIccid, newIccid }`, not an `items[]` array,
 * so it is single-SIM by construction and never appears in the bulk bar.
 */
export function SwapDialog(
  props: CommonDialogProps & {
    sim: SimSummary;
    onConfirm(input: { existingIccid: string; newIccid: string; reason?: string }): void;
  },
): ReactElement | null {
  const [newIccid, setNewIccid] = useState('');
  const [reason, setReason] = useState('');
  const existingIccid = props.sim.iccid ?? '';

  return (
    <ActionDialog
      action={SIM_ACTIONS.swap}
      open={props.open}
      targets={[props.sim]}
      submitDisabled={newIccid.trim().length === 0}
      {...(props.busy === undefined ? {} : { busy: props.busy })}
      {...(props.error === undefined ? {} : { error: props.error })}
      onConfirm={() =>
        props.onConfirm({
          existingIccid,
          newIccid: newIccid.trim(),
          ...(reason ? { reason } : {}),
        })
      }
      onCancel={props.onCancel}
    >
      <div className="flex flex-col gap-3">
        <Field label="Current SIM number (ICCID)">
          {(fieldProps) => <Input {...fieldProps} value={existingIccid} readOnly />}
        </Field>
        <Field
          label="New SIM number (ICCID)"
          hint="The number printed on the replacement SIM."
          required
        >
          {(fieldProps) => (
            <Input
              {...fieldProps}
              value={newIccid}
              inputMode="numeric"
              autoComplete="off"
              onChange={(e) => setNewIccid(e.target.value)}
            />
          )}
        </Field>
        <Field label="Reason (optional)">
          {(fieldProps) => (
            <Input {...fieldProps} value={reason} onChange={(e) => setReason(e.target.value)} />
          )}
        </Field>
      </div>
    </ActionDialog>
  );
}

/** Any action that needs a plan: activate, activate-test, from-test, change-plan. */
export function PlanActionDialog(
  props: CommonDialogProps & {
    action: SimActionDef;
    plans: PlanSummary[];
    onConfirm(input: { planCode: string; consent: boolean }): void;
  },
): ReactElement | null {
  const [planCode, setPlanCode] = useState<string | null>(null);
  const [consent, setConsent] = useState(false);

  return (
    <ActionDialog
      action={props.action}
      open={props.open}
      targets={props.targets}
      {...(props.excluded ? { excluded: props.excluded } : {})}
      consent={consent}
      onConsentChange={setConsent}
      submitDisabled={!planCode}
      {...(props.busy === undefined ? {} : { busy: props.busy })}
      {...(props.error === undefined ? {} : { error: props.error })}
      onConfirm={() => props.onConfirm({ planCode: planCode ?? '', consent })}
      onCancel={props.onCancel}
    >
      <PlanPicker plans={props.plans} value={planCode} onChange={setPlanCode} />
    </ActionDialog>
  );
}
